import { ref, computed } from 'vue';

export interface MenuItem {
  id: number;
  name: string;
  price: number;
  discount_price?: number;
  has_variants: boolean;
  category?: { name: string } | string;
  variants?: any[];
}

export function useMenuCategories(menuItems: any) {
  const expandedCategories = ref<Set<string>>(new Set());

  // Helper function to format category name
  const formatCategoryName = (name: string): string => {
    if (name === 'Uncategorized') return name;
    // Convert to proper case: first letter uppercase, rest lowercase
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Group menu items by category
  const menuItemsByCategory = computed(() => {
    const groups: Record<string, any[]> = {};
    menuItems.value.forEach((item: any) => {
      let rawCategoryName = (item.category && typeof item.category === 'object' && item.category.name) 
        ? item.category.name 
        : (item.category || 'Uncategorized');
      
      const formattedCategoryName = formatCategoryName(rawCategoryName);
      
      if (!groups[formattedCategoryName]) {
        groups[formattedCategoryName] = [];
      }
      groups[formattedCategoryName].push(item);
    });
    return groups;
  });

  // Get sorted category names
  const categoryNames = computed(() => {
    return Object.keys(menuItemsByCategory.value).sort();
  });

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    if (expandedCategories.value.has(category)) {
      expandedCategories.value.delete(category);
    } else {
      expandedCategories.value.add(category);
    }
  };

  // Check if category is expanded
  const isCategoryExpanded = (category: string): boolean => {
    return expandedCategories.value.has(category);
  };

  // Expand all categories
  const expandAllCategories = () => {
    expandedCategories.value = new Set(categoryNames.value);
  };

  // Collapse all categories
  const collapseAllCategories = () => {
    expandedCategories.value.clear();
  };

  return {
    expandedCategories,
    menuItemsByCategory,
    categoryNames,
    toggleCategory,
    isCategoryExpanded,
    expandAllCategories,
    collapseAllCategories
  };
}
